export interface DragDropZoneOptions {
  /** Called whenever the drag state changes (true = a file drag is in progress). */
  onActiveChange: (active: boolean) => void;
  /** Called when a drag is dropped inside the window. */
  onDrop: (files: FileList, text: string) => void;
  /** Optional gate — when it returns false the zone is inert. */
  isEnabled?: () => boolean;
}

/**
 * Reliable full-window drag & drop zone.
 *
 * All events are listened for at the `window` level so the zone keeps working
 * no matter what is on top of it (e.g. a full-screen image viewer). The zone
 * is hidden as soon as the drag ends, covering every outcome:
 *
 *  - dropped inside the window  -> `drop` bubbles up to `window`
 *  - dropped on the desktop / another app, or the pointer leaves the window
 *    -> `dragleave` fires with the pointer at the window edge
 *  - drag cancelled (Esc) or dropped outside without a clean event
 *    -> a short grace timer auto-hides the zone once drag activity stops
 *
 * This guarantees the overlay can never remain stuck.
 */
export class DragDropZone {
  private _opts: DragDropZoneOptions;
  private _active = false;
  private _hideTimer: number | null = null;

  /** How long without any drag activity before we force-hide (covers Esc-cancel). */
  private static readonly HIDE_GRACE_MS = 500;

  constructor(opts: DragDropZoneOptions) {
    this._opts = opts;
  }

  attach(): void {
    window.addEventListener('dragenter', this._onDragEnter);
    window.addEventListener('dragover', this._onDragOver);
    window.addEventListener('dragleave', this._onDragLeave);
    window.addEventListener('drop', this._onDrop);
  }

  detach(): void {
    window.removeEventListener('dragenter', this._onDragEnter);
    window.removeEventListener('dragover', this._onDragOver);
    window.removeEventListener('dragleave', this._onDragLeave);
    window.removeEventListener('drop', this._onDrop);
    this._clearHideTimer();
    if (this._active) {
      this._active = false;
      this._opts.onActiveChange(false);
    }
  }

  private _isEnabled(): boolean {
    return this._opts.isEnabled ? this._opts.isEnabled() : true;
  }

  private _isFileDrag(e: DragEvent): boolean {
    return !!e.dataTransfer?.types?.includes('Files');
  }

  private _setActive(active: boolean): void {
    if (this._active === active) return;
    this._active = active;
    this._opts.onActiveChange(active);
  }

  /**
   * Keep the zone alive and (re)arm the safety timer. Any drag activity resets
   * it, so the zone only force-hides after a stretch of total silence — which
   * means the drag has ended (cancelled or dropped outside the window).
   */
  private _touch(): void {
    this._clearHideTimer();
    this._hideTimer = window.setTimeout(
      () => this._setActive(false),
      DragDropZone.HIDE_GRACE_MS,
    );
  }

  private _clearHideTimer(): void {
    if (this._hideTimer != null) {
      clearTimeout(this._hideTimer);
      this._hideTimer = null;
    }
  }

  private _onDragEnter = (e: DragEvent): void => {
    if (!this._isEnabled() || !this._isFileDrag(e)) return;
    e.preventDefault();
    this._touch();
    this._setActive(true);
  };

  private _onDragOver = (e: DragEvent): void => {
    if (!this._isEnabled() || !this._isFileDrag(e)) return;
    // Prevent the browser default (navigating to the dropped file) and allow the drop.
    e.preventDefault();
    this._touch();
    this._setActive(true);
  };

  private _onDragLeave = (e: DragEvent): void => {
    // Moving to a child element keeps `relatedTarget` inside the document.
    const related = e.relatedTarget as Node | null;
    if (related && document.contains(related)) {
      this._touch();
      return;
    }
    // Pointer reached the window edge -> the drag left the window.
    const atEdge =
      e.clientX <= 0 ||
      e.clientY <= 0 ||
      e.clientX >= window.innerWidth ||
      e.clientY >= window.innerHeight;
    if (atEdge) {
      this._clearHideTimer();
      this._setActive(false);
    } else {
      // Still inside the window (e.g. Firefox leaves relatedTarget null):
      // keep the zone alive; the grace timer catches a cancelled drag.
      this._touch();
    }
  };

  private _onDrop = (e: DragEvent): void => {
    this._clearHideTimer();
    this._setActive(false);
    if (!this._isEnabled()) return;
    e.preventDefault();
    const dt = e.dataTransfer;
    if (!dt) return;
    this._opts.onDrop(dt.files, dt.getData('text/plain'));
  };
}
